<template>
  <div class="space-y-6">
    <!-- Welcome Section -->
    <div class="bg-white rounded-lg shadow-sm p-6">
      <h1 class="text-2xl font-bold text-gray-900 mb-2">Welcome back!</h1>
      <p class="text-gray-600">Here's what's happening with your email campaigns today.</p>
    </div>

    <!-- Campaigns Section -->
    <div class="bg-white rounded-lg shadow-sm">
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900">Recent Campaigns</h2>
          <router-link
            to="/campaigns"
            class="text-primary-600 hover:text-primary-700 font-medium text-sm"
          >
            View all campaigns →
          </router-link>
        </div>
      </div>
      
      <div class="p-6">
        <div v-if="campaignsLoading" class="flex justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
        
        <div v-else-if="campaigns.length === 0" class="text-center py-8">
          <EnvelopeIcon class="mx-auto h-12 w-12 text-gray-400" />
          <h3 class="mt-2 text-sm font-medium text-gray-900">No campaigns yet</h3>
          <p class="mt-1 text-sm text-gray-500">Get started by creating your first email campaign.</p>
          <div class="mt-6">
            <router-link
              to="/campaigns"
              class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              Create Campaign
            </router-link>
          </div>
        </div>
        
        <div v-else class="space-y-4">
          <div
            v-for="campaign in campaigns"
            :key="campaign.CampaignID"
            class="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <div class="flex items-center space-x-4">
              <div
                class="w-3 h-3 rounded-full"
                :class="getCampaignStatusColor(campaign.Status)"
              ></div>
              <div>
                <h3 class="font-medium text-gray-900">{{ campaign.CampaignName }}</h3>
                <p class="text-sm text-gray-500">{{ campaign.Subject }}</p>
                <div class="flex items-center space-x-4 mt-1 text-xs text-gray-400">
                  <span>{{ formatDate(campaign.ScheduledStart) }}</span>
                  <span>•</span>
                  <span>{{ campaign.TotalEmailsSent }} sent</span>
                  <span v-if="campaign.TotalEmailsBounced > 0" class="text-red-500">
                    • {{ campaign.TotalEmailsBounced }} bounced
                  </span>
                </div>
              </div>
            </div>
            
            <div class="flex items-center space-x-2">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="getCampaignStatusBadgeClass(campaign.Status)"
              >
                {{ campaign.Status }}
              </span>
              <router-link
                :to="`/campaigns/${campaign.CampaignID}`"
                class="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                View Details →
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Lists Section -->
    <div class="bg-white rounded-lg shadow-sm">
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900">Email Lists</h2>
          <router-link
            to="/lists"
            class="text-primary-600 hover:text-primary-700 font-medium text-sm"
          >
            Manage lists →
          </router-link>
        </div>
      </div>
      
      <div class="p-6">
        <div v-if="listsLoading" class="flex justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
        
        <div v-else-if="emailLists.length === 0" class="text-center py-8">
          <ListBulletIcon class="mx-auto h-12 w-12 text-gray-400" />
          <h3 class="mt-2 text-sm font-medium text-gray-900">No email lists yet</h3>
          <p class="mt-1 text-sm text-gray-500">Create your first email list to start building your audience.</p>
          <div class="mt-6">
            <router-link
              to="/lists"
              class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              Create List
            </router-link>
          </div>
        </div>
        
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="list in emailLists"
            :key="list.ListID"
            class="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <h3 class="font-medium text-gray-900">{{ list.ListName }}</h3>
                <p v-if="list.Description" class="text-sm text-gray-500 mt-1">
                  {{ list.Description }}
                </p>
                <div class="flex items-center space-x-2 mt-2">
                  <span class="text-sm text-gray-400">{{ list.TotalEmails }} subscribers</span>
                  <span v-if="list.ClubName" class="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                    {{ list.ClubName }}
                  </span>
                </div>
              </div>
              <router-link
                :to="`/lists/${list.ListID}`"
                class="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                Edit
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Stats -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <EnvelopeIcon class="h-8 w-8 text-primary-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Active Campaigns</p>
            <p class="text-2xl font-semibold text-gray-900">
              {{ activeCampaignsCount }}
            </p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <ListBulletIcon class="h-8 w-8 text-success-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Total Lists</p>
            <p class="text-2xl font-semibold text-gray-900">
              {{ emailLists.length }}
            </p>
          </div>
        </div>
      </div>
      
      <div class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <GlobeAltIcon class="h-8 w-8 text-warning-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Domains</p>
            <p class="text-2xl font-semibold text-gray-900">
              {{ domainsCount }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'
import { campaignsApi, listsApi, domainsApi } from '../services/api'
import { toast } from 'vue3-toastify'
import {
  EnvelopeIcon,
  ListBulletIcon,
  GlobeAltIcon
} from '@heroicons/vue/24/outline'

const { getAccessTokenSilently } = useAuth0()

// State
const campaigns = ref([])
const emailLists = ref([])
const domains = ref([])
const campaignsLoading = ref(false)
const listsLoading = ref(false)
const domainsLoading = ref(false)

// Computed
const activeCampaignsCount = computed(() => {
  return campaigns.value.filter(c => 
    ['Scheduled', 'Sending'].includes(c.Status)
  ).length
})

const domainsCount = computed(() => domains.value.length)

// Methods
const getCampaignStatusColor = (status) => {
  switch (status) {
    case 'Scheduled':
      return 'bg-warning-500'
    case 'Sending':
      return 'bg-success-500'
    case 'Completed':
      return 'bg-gray-400'
    case 'Paused':
      return 'bg-gray-300'
    default:
      return 'bg-gray-400'
  }
}

const getCampaignStatusBadgeClass = (status) => {
  switch (status) {
    case 'Scheduled':
      return 'bg-warning-100 text-warning-800'
    case 'Sending':
      return 'bg-success-100 text-success-800'
    case 'Completed':
      return 'bg-gray-100 text-gray-800'
    case 'Paused':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatDate = (dateString) => {
  if (!dateString) return 'Not scheduled'
  return new Date(dateString).toLocaleDateString()
}

const fetchCampaigns = async () => {
  try {
    campaignsLoading.value = true
    const token = await getAccessTokenSilently()
    const response = await campaignsApi.getAll()
    campaigns.value = response.data.data || []
  } catch (error) {
    console.error('Error fetching campaigns:', error)
    toast.error('Failed to fetch campaigns')
  } finally {
    campaignsLoading.value = false
  }
}

const fetchLists = async () => {
  try {
    listsLoading.value = true
    const response = await listsApi.getAll()
    emailLists.value = response.data.data || []
  } catch (error) {
    console.error('Error fetching lists:', error)
    toast.error('Failed to fetch email lists')
  } finally {
    listsLoading.value = false
  }
}

const fetchDomains = async () => {
  try {
    domainsLoading.value = true
    const response = await domainsApi.getAll()
    domains.value = response.data.data || []
  } catch (error) {
    console.error('Error fetching domains:', error)
    toast.error('Failed to fetch domains')
  } finally {
    domainsLoading.value = false
  }
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchCampaigns(),
    fetchLists(),
    fetchDomains()
  ])
})
</script> 